/*
 * MySiteCare
 *
 * Copyright © 2026 Jeanjen Sisoy Luberio
 * doing business as MySiteCare Website Application Services.
 * All rights reserved.
 *
 * DTI Business Name No. 7934250
 * https://mysitecare.eu
 *
 * This file contains original proprietary MySiteCare source code,
 * except for third-party components identified under their
 * respective licenses.
 */
import { useEffect, useState } from "react";
import SiteDashboardShell, { useSiteDashboard } from "@/components/customer/SiteDashboardShell";
import { fetchSiteAnalytics } from "@/lib/api";

const RANGES = [
  ["today", "Today"], ["7d", "Last 7 Days"], ["30d", "Last 30 Days"],
  ["this_month", "This Month"], ["last_month", "Last Month"], ["this_year", "This Year"],
];

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-[#94A3B8]">{label}</p>
      <p className="mt-1.5 text-2xl font-bold font-display text-[#0F172A]">{value}</p>
    </div>
  );
}

export default function SiteAnalytics() {
  const dashboard = useSiteDashboard();
  const { token, slug } = dashboard;
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);

  useEffect(() => { if (token) fetchSiteAnalytics(token, slug, range).then(setData).catch(() => {}); }, [token, slug, range]);

  return (
    <SiteDashboardShell active="analytics" dashboard={dashboard}>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="text-lg font-bold font-display text-[#0F172A]">Website Performance</h2>
        <select value={range} onChange={(e) => setRange(e.target.value)} className="rounded-lg border border-[#E2E8F0] bg-white px-3 py-1.5 text-sm">
          {RANGES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>

      {!data ? <p className="text-sm text-[#475569]">Loading…</p> : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Stat label="Visitors (pageviews)" value={data.pageviews.toLocaleString()} />
            <Stat label="Form submissions / inquiries" value={data.form_submissions.toLocaleString()} />
            <Stat label="Appointment requests" value={data.appointment_requests.toLocaleString()} />
            <Stat label="Confirmed appointments" value={data.confirmed_appointments.toLocaleString()} />
            <Stat label="New leads" value={data.new_leads.toLocaleString()} />
            <Stat label="Visitor → confirmed conversion" value={data.conversion_rate_pct != null ? `${data.conversion_rate_pct}%` : "—"} />
          </div>
          {data.pageviews === 0 && data.form_submissions === 0 && (
            <p className="mt-4 text-sm text-[#94A3B8]">No visitor activity recorded yet for this period.</p>
          )}
          <p className="mt-6 text-xs text-[#94A3B8]">Traffic-source and device-type breakdowns aren't collected yet — only the metrics above are actually measured.</p>
        </>
      )}
    </SiteDashboardShell>
  );
}
